// Instruction screen (the `instruction` step). Twin of the desktop
// InstructionPage. Full-bleed white: optional centered title, a body of
// researcher-typed Markdown sitting straight on the page (no card or border),
// and one button (bottom right) to move on.
//
// Parity with instruction_page.py / instruction_page_style.qss:
//   - title is hidden when blank.
//   - body_text is Markdown (same engine as the writing prompt); it scrolls
//     inside the reading column when long. Links open in a new tab so a click
//     never navigates the participant out of the study (desktop opens them in
//     the external browser).
//   - button_label blank or missing falls back to "Continue".
//   - min_read_seconds: the button is disabled until it elapses (null = none).
//   - pause_after_seconds keeps the page up before advancing.
//   - No timed auto-advance and no mid-block surveys (Transition covers timed).
//   - Submit payload: { title, time_on_block_seconds, end_trigger: "continue" }.

import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { WebRendererProps } from "./registry";
import type { InstructionResult } from "../api/types";

const DEFAULT_BUTTON_LABEL = "Continue"; // matches desktop _DEFAULT_BUTTON_LABEL

export default function InstructionRenderer({ step, onDone }: WebRendererProps) {
  const settings = (step.settings ?? {}) as Record<string, unknown>;

  const title = typeof settings.title === "string" ? settings.title : "";
  const bodyText = typeof settings.body_text === "string" ? settings.body_text : "";
  const buttonLabel =
    (typeof settings.button_label === "string" ? settings.button_label.trim() : "") ||
    DEFAULT_BUTTON_LABEL;
  const minReadSeconds =
    typeof settings.min_read_seconds === "number" ? settings.min_read_seconds : 0;
  const pauseAfter =
    typeof settings.pause_after_seconds === "number" ? settings.pause_after_seconds : 0;

  const [continueEnabled, setContinueEnabled] = useState(!(minReadSeconds > 0));
  const [submitted, setSubmitted] = useState(false);
  const lockedRef = useRef(false);
  const startedAt = useRef<number>(performance.now());

  // Minimum-read gate: hold the button until min_read_seconds elapses.
  useEffect(() => {
    if (!(minReadSeconds > 0)) return;
    const gate = window.setTimeout(() => setContinueEnabled(true), minReadSeconds * 1000);
    return () => clearTimeout(gate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function finish() {
    if (lockedRef.current) return; // re-entrance guard: a double click submits once
    lockedRef.current = true;
    setSubmitted(true);
    const elapsed = (performance.now() - startedAt.current) / 1000;
    const result: InstructionResult = {
      title,
      time_on_block_seconds: Math.round(elapsed * 1000) / 1000,
      end_trigger: "continue",
    };
    const submit = () => onDone({ kind: "submit", result });
    if (pauseAfter > 0) window.setTimeout(submit, pauseAfter * 1000);
    else submit();
  }

  return (
    <div className="jol-instruction">
      <div className="jol-instruction-column">
        {title.trim() ? <div className="jol-writing-title">{title}</div> : null}

        <div className="jol-instruction-body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              a: ({ node: _node, ...props }) => (
                <a {...props} target="_blank" rel="noopener noreferrer" />
              ),
            }}
          >
            {bodyText}
          </ReactMarkdown>
        </div>

        <div className="jol-instruction-footer">
          <button
            type="button"
            className="jol-writing-submit"
            disabled={!continueEnabled || submitted}
            onClick={finish}
          >
            {buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
